import { Badge } from "@/components/ui/badge";
import { Deal } from "@/core/entities/deal";
import {
  Event,
  EventType,
  PaymentMethod,
  type BuySubmissionFormEvent,
  type SecureQuoteFormSubmissionEvent,
  type SellSubmissionFormEvent,
  type WebflowFormSubmissionEvent,
} from "@/core/entities/event";
import { Product, ProductType } from "@/core/entities/product";
import { formatDistanceToNow, fromUnixTime, isValid } from "date-fns";
import {
  Calendar,
  FileText,
  Rss,
  Shield,
  ShoppingCart,
  TrendingUp,
} from "lucide-react";

// Date and Time Utilities
export const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (
    typeof value === "object" &&
    value !== null &&
    "seconds" in (value as Record<string, unknown>) &&
    typeof (value as { seconds?: unknown }).seconds === "number"
  ) {
    return fromUnixTime((value as { seconds: number }).seconds);
  }
  const date = new Date(value as string | number | Date);
  return isValid(date) ? date : null;
};

export const getCurrentStageStartDate = (deal: Deal): Date => {
  return (
    toDate(deal.stageHistory?.currentStageEnteredAt) ||
    toDate(deal.createdAt) ||
    new Date()
  );
};

export const getStageDurationText = (deal: Deal): string => {
  const startDate = getCurrentStageStartDate(deal);
  return formatDistanceToNow(startDate, { includeSeconds: false });
};

// Event Type Utilities
export const getEventTypeIcon = (type: EventType) => {
  switch (type) {
    case EventType.WEBFLOW_FORM_SUBMISSION:
      return <FileText className="h-4 w-4 text-blue-600" />;
    case EventType.SECURE_QUOTE:
      return <Shield className="h-4 w-4 text-green-600" />;
    case EventType.SELL_SUBMISSION_FORM:
      return <TrendingUp className="h-4 w-4 text-orange-600" />;
    case EventType.BUY_SUBMISSION_FORM:
      return <ShoppingCart className="h-4 w-4 text-purple-600" />;
    case EventType.HUBSPOT_UPDATE:
      return <Rss className="h-4 w-4 text-indigo-600" />;
    default:
      return <Calendar className="h-4 w-4 text-gray-600" />;
  }
};

export const getEventTypeBadgeVariant = (type: EventType) => {
  switch (type) {
    case EventType.WEBFLOW_FORM_SUBMISSION:
      return "secondary";
    case EventType.SECURE_QUOTE:
      return "default";
    case EventType.SELL_SUBMISSION_FORM:
      return "destructive";
    case EventType.BUY_SUBMISSION_FORM:
      return "outline";
    case EventType.HUBSPOT_UPDATE:
      return "secondary";
    default:
      return "secondary";
  }
};

export const formatEventType = (type: EventType) => {
  switch (type) {
    case EventType.WEBFLOW_FORM_SUBMISSION:
      return "Webflow Form";
    case EventType.SECURE_QUOTE:
      return "Secure Quote";
    case EventType.SELL_SUBMISSION_FORM:
      return "Sell Form";
    case EventType.BUY_SUBMISSION_FORM:
      return "Buy Form";
    case EventType.HUBSPOT_UPDATE:
      return "HubSpot Update";
    default:
      return type;
  }
};

// Data Extraction Utilities
export const getProductName = (
  productId: string | null,
  products: Product[],
) => {
  if (!productId) return null;
  const product = products.find((p) => p.id === productId);
  return product?.name ?? null;
};

export const getOrderType = (event: Event) => {
  switch (event.type) {
    case EventType.WEBFLOW_FORM_SUBMISSION:
      return (event as WebflowFormSubmissionEvent).data?.buyOrSell ?? null;
    case EventType.SECURE_QUOTE:
      return (event as SecureQuoteFormSubmissionEvent).data?.type ?? null;
    case EventType.SELL_SUBMISSION_FORM:
      return ProductType.SELL;
    case EventType.BUY_SUBMISSION_FORM:
      return ProductType.BUY;
    default:
      return null;
  }
};

export const getMiles = (event: Event) => {
  if (event.type === EventType.WEBFLOW_FORM_SUBMISSION) {
    return (event as WebflowFormSubmissionEvent).data?.amount ?? null;
  }
  if (event.type === EventType.SECURE_QUOTE) {
    return (event as SecureQuoteFormSubmissionEvent).data?.miles ?? null;
  }
  if (event.type === EventType.SELL_SUBMISSION_FORM) {
    return (event as SellSubmissionFormEvent).data?.miles ?? null;
  }
  if (event.type === EventType.BUY_SUBMISSION_FORM) {
    return (event as BuySubmissionFormEvent).data?.miles ?? null;
  }
  return null;
};

export const getRate = (event: Event) => {
  if (event.type === EventType.SECURE_QUOTE) {
    return (event as SecureQuoteFormSubmissionEvent).data?.averageRate ?? null;
  }
  if (event.type === EventType.SELL_SUBMISSION_FORM) {
    return (event as SellSubmissionFormEvent).data?.averageRate ?? null;
  }
  if (event.type === EventType.BUY_SUBMISSION_FORM) {
    return (event as BuySubmissionFormEvent).data?.averageRate ?? null;
  }
  return null;
};

export const getTotalPayout = (event: Event) => {
  if (event.type === EventType.SECURE_QUOTE) {
    return (event as SecureQuoteFormSubmissionEvent).data?.totalPayout ?? null;
  }
  if (event.type === EventType.SELL_SUBMISSION_FORM) {
    return (event as SellSubmissionFormEvent).data?.totalPayout ?? null;
  }
  if (event.type === EventType.BUY_SUBMISSION_FORM) {
    return (event as BuySubmissionFormEvent).data?.totalPayout ?? null;
  }
  return null;
};

export const getPaymentMethod = (event: Event) => {
  if (event.type === EventType.SELL_SUBMISSION_FORM) {
    return (event as SellSubmissionFormEvent).data?.paymentMethod ?? null;
  }
  return null;
};

// Formatting Utilities
export const formatCurrency = (amount: number | null) => {
  if (amount === null || amount === undefined) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

export const formatMiles = (miles: number | null) => {
  if (miles === null || miles === undefined) return "-";
  return new Intl.NumberFormat("en-US").format(miles);
};

export const formatRate = (rate: number | null) => {
  if (rate === null || rate === undefined) return "-";
  return `$${rate}`;
};

export const formatPaymentMethod = (method: PaymentMethod | null) => {
  if (!method) return "-";
  switch (method) {
    case PaymentMethod.PAYPAL:
      return "PayPal";
    case PaymentMethod.ECHECK:
      return "eCheck";
    case PaymentMethod.WIRE:
      return "Wire Transfer";
    default:
      return method;
  }
};

// Badge and Icon Utilities
export const getOrderTypeIcon = (orderType: ProductType | null) => {
  switch (orderType) {
    case ProductType.BUY:
      return <TrendingUp className="h-4 w-4 text-green-600" />;
    case ProductType.SELL:
      return <TrendingUp className="h-4 w-4 text-green-600" />;
    default:
      return null;
  }
};

export const getBuyOrSellBadge = (buyOrSell: string) => {
  switch (buyOrSell.toLowerCase()) {
    case "sell":
      return (
        <Badge className="bg-muted text-secondary-foreground hover:bg-muted">
          Sell
        </Badge>
      );
    case "buy":
      return (
        <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary">
          Buy
        </Badge>
      );
    default:
      return (
        <Badge className="bg-primary/10 text-primary hover:bg-primary/10">
          {buyOrSell}
        </Badge>
      );
  }
};

export const getStageBadge = (stage: string) => {
  switch (stage.toLowerCase()) {
    case "closed_won":
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
          Closed Won
        </Badge>
      );
    case "closed_lost":
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
          Closed Lost
        </Badge>
      );
    default:
      return (
        <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
          {stage}
        </Badge>
      );
  }
};

// Additional Utilities for EventsDialog
export const getOrderTypeBadgeVariant = (orderType: ProductType | null) => {
  switch (orderType) {
    case ProductType.BUY:
      return "default";
    case ProductType.SELL:
      return "default";
    default:
      return "outline";
  }
};
